import { SliEvents } from '@metrics/sliConstants';

/**
 * An enum representing the current reporters supported to identify for which reporter the data transformation
 * should take place
 */
export enum Reporter {
  ANALYTICS, // the analytics reporter covers Mixpanel, Analytics and AttendeeActivities destinations
  DATADOG,
  RUDDERSTACK
}

/**
 * A type for defining data transformers for SLI metrics. Can be used to clean and/or transform the metrics data if
 * necessary before sending the data to the destinations.
 */
export type MetricDataTransformer = (data: Record<string, unknown>) => Record<string, unknown>;

/**
 * A noop transformer that returns the original data
 *
 * @param data
 */
const noOpTransformer = (data: Record<string, unknown>) => data;

/**
 * Divides the duration (amount of time attendee watched the livestream) with the session duration and adds the new
 * value to the existing data before data is set to the reporter
 *
 * @param data The data to transform
 */
const livestreamLivecycleTransformer = (data: Record<string, unknown>): Record<string, unknown> => {
  const duration = Number(data?.duration) || 0;
  const sessionDuration = Number(data?.sessionDuration) || 0;
  const presentationDuration = sessionDuration > 0 ? duration / sessionDuration : 1;

  return {
    ...data,
    presentationDuration: Number(presentationDuration.toFixed(2))
  };
};

/**
 * Data cleanup for analytics reporter
 * @param data Data to cleanup
 */
const cleanAnalyticsData = (data: Record<string, unknown>): Record<string, unknown> => {
  // Analytics has its own type prop that overrides the type from the page-actions API (i.e. tracked, abandoned, etc)
  // We do not require to pass destinations, attributes, and pageTimings to analytics
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { destinations, attributes, pageTimings, ...rest } = data;

  return { ...rest };
};

/**
 * Data cleanup for datadog reporter
 * @param data Data to cleanup
 */
const cleanDatadogData = (data: Record<string, unknown>): Record<string, unknown> => {
  // Remove unnecessary fields set in the event definition
  // eslint-disable-next-line camelcase, @typescript-eslint/no-unused-vars
  const { attributes, objectIdAttribute, ...rest } = data;

  // Due to 'type' being a reserved tag in SLI metrics, we converted type to error_type for some error cases.
  // We want to convert the error_type back to type before sending data to Datadog
  if (rest.error_type) {
    rest.type = rest.error_type;
    delete rest.error_type;
  }

  return { ...rest };
};

/**
 * Data cleanup for rudderstack reporter
 * @param data Data to cleanup
 */
const cleanRudderstackData = (data: Record<string, unknown>): Record<string, unknown> => {
  // Remove unnecessary fields set in the event definition
  // the id field is a reserved word in Rudderstack and it will log an error message if encountered
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pageTimings, id, ...rest } = data;

  return { ...rest };
};

/**
 * Return a MetricDataTransformer for an SLI event
 *
 * @param reporter The reporter to transform data for.
 * @param sliId The SLI id of the transformer
 */
export const getTransformer = (reporter: Reporter, sliId: string): MetricDataTransformer => {
  let transformer;
  switch (sliId) {
    case SliEvents.LIVESTREAM_LIFECYCLE.id:
      transformer = livestreamLivecycleTransformer;
      break;
    default:
      transformer = noOpTransformer;
  }

  return (data: Record<string, unknown>) => {
    const transformedData = transformer(data);
    switch (reporter) {
      case Reporter.ANALYTICS:
        return { ...cleanAnalyticsData(transformedData) };
      case Reporter.DATADOG:
        return { ...cleanDatadogData(transformedData) };
      case Reporter.RUDDERSTACK:
        return { ...cleanRudderstackData(transformedData) };
      default:
        throw Error(`Unsupported reporter: ${reporter}`);
    }
  };
};
